const GiftCardAddBaseController = require('../../wallet/giftcard/gift-card-add-base.component').GiftCardAddBaseController;

const ADD_GIFT_CARD_ID = 'GiftCard:AddGiftCard:SplitPayment';
const ADD_GIFT_CARD_BALANCE_ID = 'GiftCard:AddGiftCard:BalanceLoading';

const { replace, some } = require('lodash');

class GiftCardAddPaymentController extends GiftCardAddBaseController {
    constructor($filter, $timeout, settings, announcerService, digitalPayPaymentInstrumentsService, digitalPayFormValidator, digitalPayPaymentErrorDisplayService) {
        super($timeout, settings, digitalPayPaymentInstrumentsService, digitalPayFormValidator);
        this.$filter = $filter;
        this.settings = settings;
        this.announcerService = announcerService;
        this.digitalPayPaymentInstrumentsService = digitalPayPaymentInstrumentsService;
        this.digitalPayFormValidator = digitalPayFormValidator;
        this.digitalPayPaymentErrorDisplayService = digitalPayPaymentErrorDisplayService;
    }

    $onInit() {
        super.$onInit();

        this.isLoadingBalance = false;
        this.giftCardNumberFocus = false;
        this.isImpersonated = false;

        this.onFormValidityChange({ isValid: this.hasValidGiftCardBalance || !this.card.BalanceApiAvailable });
    }

    $onDestroy() {
        this.onFormValidityChange({ isValid: true });
        this.paymentStateDefinitions.removeDisablePaymentState(ADD_GIFT_CARD_ID);
        this.paymentStateDefinitions.removeDisablePaymentState(ADD_GIFT_CARD_BALANCE_ID);
    }

    onBlurEvents(event) {
        if (this.checkCardNumberAlreadyExists(this.card.CardNumber)) {
            this.setDuplicateCardError(true);
        } else {
            this.giftCardCaptureFocusLost(event);
        }
    }

    onFocusGiftCardNumber(event) {
        if (this.checkCardNumberAlreadyExists(this.card.CardNumber)) {
            this.setDuplicateCardError(false);
        } else {
            this.giftCardCaptureFocusGain(event);
        }
    }

    onFocusPin(event) {
        if (this.checkCardNumberAlreadyExists(this.card.CardNumber)) {
            this.setDuplicateCardError(true);
        } else {
            this.giftCardCaptureFocusGain(event);
        }
    }

    onChangeGiftCardNumber() {
        this.digitalPayPaymentErrorDisplayService.clearGlobalError();
        // Timeout added to stop the red flash in the gift card number input field when retrieving gift card balance
        this.$timeout(() => {
            this.getBalance();
        });
    }

    onChangePin() {
        this.digitalPayPaymentErrorDisplayService.clearGlobalError();
        // Timeout added to stop the red flash in the gift card pin input field when retrieving gift card balance
        this.$timeout(() => {
            this.getBalance();
        });
    }

    onChangeCardAmount() {
        this.digitalPayPaymentErrorDisplayService.clearGlobalError();

        if (this.shouldDisableCheckBox()) {
            this.card.IsSavingCard = false;
        }

        const hasValidBalance = this.hasValidBalance();
        if (!hasValidBalance) {
            return;
        } else if (this.card.Amount && !hasValidBalance) {
            this.giftCardCaptureFocusLost({ target: { name: this.settings.digitalPayGiftCardFields.amount } });
            return;
        } else if (!this.card.Amount && (!this.focusLostObject || this.focusLostObject.allFieldsEmpty)) {
            this.errorMessage = '';
            return;
        }

        const amount = this.getGiftCardAmount(this.card.Amount);

        this.errorMessage = amount > parseFloat(this.card.Balance) && !this.isCardUnavailable(this.card)
            ? replace(this.settings.digitalPayError.giftCardAmountAvailableExceedMessage, '{AMOUNTAVAILABLE}', this.$filter('currency')(this.card.Balance))
            : '';

        this.onChangeGiftCardAmount({ amount, card: this.card });

        if (this.card.Amount) {
            this.paymentStateDefinitions.removeDisablePaymentState(ADD_GIFT_CARD_ID);
        } else {
            this.paymentStateDefinitions.addDisablePaymentState(ADD_GIFT_CARD_ID, this.settings.digitalPayScreenReaderMessages.giftCardAddDisableMessage);
        }

        this.onFormValidityChange({ isValid: this.amountIsValid() && (this.hasValidGiftCardBalance || !this.card.BalanceApiAvailable) });
    }

    onFocusCardAmount() {
        this.onChangeCardAmount();
    }

    setDuplicateCardError(isDuplicate) {
        this.errorMessage = isDuplicate
            ? this.settings.digitalPayError.giftCardAddDuplicateCardMessage
            : '';
        this.giftCardAddForm.giftCardNumber.$setValidity('duplicate', !isDuplicate);
    }

    shouldHideSave() {
        return this.isImpersonated;
    }

    checkCardNumberAlreadyExists(cardNumber) {
        return some(this.usedCardNumbers, usedCardNumber => usedCardNumber === cardNumber);
    }

    getGiftCardAmount(amount) {
        return amount ? parseFloat(amount) : 0.0;
    }

    isCardUnavailable(giftCard) {
        return giftCard.ErrorCode === this.settings.digitalPayErrorCodes.giftCardKnownBalanceCheckFailed ||
            giftCard.ErrorCode === this.settings.digitalPayErrorCodes.giftCardUnknownBalanceCheckFailed ||
            giftCard.ErrorCode === this.settings.digitalPayErrorCodes.giftCardBalanceApiUnavailable;
    }

    onKeyGiftCardStateEvents() {
        if (this.isValueUndefinedOrEmpty(this.giftCardAddForm.giftCardNumber.$viewValue)
            || this.isValueUndefinedOrEmpty(this.giftCardAddForm.giftCardPin.$viewValue)) {
            this.paymentStateDefinitions.addDisablePaymentState(ADD_GIFT_CARD_ID, this.settings.digitalPayScreenReaderMessages.giftCardAddDisableMessage);
            this.onChangeSelectGiftCard({ id: this.card.PaymentInstrumentId || this.card.NewStagedCardId, isSelected: true });
        } else {
            this.paymentStateDefinitions.removeDisablePaymentState(ADD_GIFT_CARD_ID);
            this.onChangeSelectGiftCard({ id: this.card.PaymentInstrumentId || this.card.NewStagedCardId, isSelected: false });
        }
    }

    isValueUndefinedOrEmpty(value) {
        return value && value.length;
    }

    clearCardCaptureContents() {
        this.invalidateBalance();
        this.card.CardNumber = '';
        this.card.Pin = '';
        this.card.IsSavingCard = false;
        this.cardNumberError = false;
        this.resetFormValidation();
        this.clearValidationState();
        this.setCardValidity(true);
        this.setDuplicateCardError(false);
        this.stopLoadingBalance();
    }

    resetFormValidation() {
        this.giftCardAddForm.$setPristine();
        this.giftCardAddForm.$setUntouched();
    }

    isFormInvalid() {
        return this.card.BalanceApiAvailable && this.giftCardAddForm.$invalid
            || this.giftCardAddForm.giftCardNumber.$invalid
            || this.giftCardAddForm.giftCardPin.$invalid
            || this.giftCardAddForm.giftCardAddAmount.$invalid;
    }

    amountIsValid() {
        return this.card.Amount && this.card.Amount > 0 && this.card.Amount <= this.card.Balance;
    }

    shouldDisableCheckBox() {
        return this.isFormInvalid()
            || !this.card.Balance
            || !this.amountIsValid()
            || this.isBalanceSameAsAvailableAmount();
    }

    /**
     * @override
     */
    getBalance() {
        if (!this.checkCardNumberAlreadyExists(this.card.CardNumber)) {
            super.getBalance();
        }
    }

    /**
     * @override
     */
    invalidateBalance() {
        super.invalidateBalance();
        this.card.IsSavingCard = false;
        this.onChangeGiftCardAmount({ amount: this.card.Amount, card: this.card });
    }

    /**
     * @override
     */
    clearValidationState() {
        super.clearValidationState();
        this.paymentStateDefinitions.removeDisablePaymentState(ADD_GIFT_CARD_ID);
        this.onChangeSelectGiftCard({ id: this.card.PaymentInstrumentId || this.card.NewStagedCardId, isSelected: false });
    }

    /**
     * @override
     */
    onGetValidBalance() {
        super.onGetValidBalance();
        this.announcerService.announce(`This gift card has $${this.card.Balance} available`);
    }

    /**
     * @override
     */
    startLoadingBalance() {
        this.isLoadingBalance = true;
        this.paymentStateDefinitions.addDisablePaymentState(ADD_GIFT_CARD_BALANCE_ID);
    }

    /**
     * @override
     */
    stopLoadingBalance() {
        this.isLoadingBalance = false;
        this.paymentStateDefinitions.removeDisablePaymentState(ADD_GIFT_CARD_BALANCE_ID);
    }
}

GiftCardAddPaymentController.$inject = ['$filter', '$timeout', 'Settings', 'AnnouncerService', 'DigitalPayPaymentInstrumentsService', 'DigitalPayFormValidator', 'DigitalPayPaymentErrorDisplayService'];

const giftCardAddPaymentComponentDefinition = {
    controller: GiftCardAddPaymentController,
    template: require('./gift-card-add-payment.component.html').trim(),
    bindings: {
        card: '<',
        allowedBins: '<',
        paymentStateDefinitions: '<',
        onChangeGiftCardAmount: '&',
        onChangeSelectGiftCard: '&',
        isImpersonated: '<',
        usedCardNumbers: '<',
        onFormValidityChange: '&',
    },
};

module.exports = {
    GiftCardAddPaymentController,
    giftCardAddPaymentComponentDefinition,
};



// WEBPACK FOOTER //
// ./src/digitalpay/giftcard/gift-card-add-payment.component.js